import { createClient } from '@supabase/supabase-js';

let cachedClient = null;

function cleanSupabaseEnvValue(value, { url = false } = {}) {
  const cleaned = String(value || '')
    .trim()
    .replace(/^['"]|['"]$/g, '')
    .replace(/\\r|\\n|\\t/g, '')
    .replace(/[\r\n\t]/g, '')
    .trim();

  return url ? cleaned.replace(/\/+$/, '') : cleaned;
}

function supabaseUrl() {
  return cleanSupabaseEnvValue(process.env.REACT_APP_SUPABASE_URL, { url: true });
}

/** Der Schluessel, unter dem supabase-js die Sitzung im Browser ablegt: sb-<projekt>-auth-token. */
export function sitzungsSchluessel(url = supabaseUrl()) {
  try {
    return `sb-${new URL(url).hostname.split('.')[0]}-auth-token`;
  } catch (_fehler) {
    return '';
  }
}

function browserSpeicher() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (_fehler) {
    return null;
  }
}

/** Entfernt die gespeicherte Sitzung dieses Supabase-Projekts (samt Code-Verifier). */
export function entferneGespeicherteSitzung(speicher = browserSpeicher(), url = supabaseUrl()) {
  const schluessel = sitzungsSchluessel(url);
  if (!schluessel || !speicher) return 0;
  const treffer = [];
  for (let i = 0; i < speicher.length; i += 1) {
    const name = speicher.key(i);
    if (name && name.startsWith(schluessel)) treffer.push(name);
  }
  treffer.forEach((name) => speicher.removeItem(name));
  return treffer.length;
}

/**
 * Abmelden, das auch wirklich abmeldet. signOut() laesst die Sitzung im Browser stehen,
 * wenn der Server sie schon nicht mehr kennt: Er antwortet 403 session_not_found, und
 * auth-js 2.69 macht daraus einen AuthSessionMissingError statt "schon abgemeldet"
 * (so am 30.09.2026 in Chrome beobachtet - etwa nachdem ein Logout in einem anderen
 * EN:POWER-Werkzeug alle Sitzungen des Kontos beendet hatte). Dann wird die Sitzung hier
 * selbst entfernt. Der Aufrufer setzt seinen Sitzungszustand danach auf null, weil in
 * diesem Fall kein SIGNED_OUT-Ereignis kommt.
 *
 * @returns {Promise<{ lokalErzwungen: boolean, fehler: Error|null }>}
 */
export async function abmelden(client, { speicher = browserSpeicher(), url = supabaseUrl() } = {}) {
  let fehler = null;
  try {
    const { error } = await client.auth.signOut();
    fehler = error || null;
  } catch (error) {
    fehler = error;
  }
  if (fehler) entferneGespeicherteSitzung(speicher, url);
  return { lokalErzwungen: Boolean(fehler), fehler };
}

export function getSupabaseClient() {
  if (cachedClient) {
    return cachedClient;
  }

  const url = supabaseUrl();
  const anonKey = cleanSupabaseEnvValue(process.env.REACT_APP_SUPABASE_ANON_KEY);

  if (!url || !anonKey) {
    return null;
  }

  cachedClient = createClient(url, anonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  });

  return cachedClient;
}
